import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { ifProp } from 'styled-tools'
import { palette } from 'styled-theme'
import { Box, Flex } from '@rebass/grid'
import { GrEdit } from 'react-icons/gr'
import { IoSaveOutline, IoShareSocialOutline } from 'react-icons/io5'
import Text from '../../atoms/Text'
import Image from '../../atoms/Image'
import Button from '../../atoms/Button'
import Select from '../../atoms/Select'
import Tooltip from '../../atoms/Tooltip'
import Modal from '../../molecules/Modal'
import { handleErrorResponse } from '../../../utlils/general'
import { downloadFile, shareFile } from '../../../utlils/file'
import { generateMyAvatar } from '../../../api/users'
import { useAuth } from '../../../contexts/auth'

const USER_AVATAR_GENERATION_COST = 10
export const LOCAL_STORAGE_KEY_PROFILE_AVATAR_REMINDER_OPENED = 'PROFILE_AVATAR_REMINDER_OPENED'
export const LOCAL_STORAGE_KEY_AVATAR_GENERATE_TOOLTIP_OPENED = 'AVATAR_GENERATE_TOOLTIP_OPENED'

const avatarStyleOptions = [
  { label: '動漫', value: 'Manga' },
  { label: '吉卜力', value: 'Ghibli' },
  { label: 'Lego', value: 'Lego Figure' },
  { label: '海賊王', value: 'One Piece' },
  { label: '火影忍者', value: 'Naruto' },
  { label: 'Chiikawa', value: 'Chiikawa' },
  { label: 'Sanrio', value: 'Sanrio' },
  { label: 'San-X', value: 'San-X' },
  { label: '多啦A夢', value: 'Doraemon' },
  { label: 'Simpson', value: 'Simpson' },
  { label: '飛天小女警', value: 'Powerpuff Girls' },
  { label: '角落生物', value: 'Sumikko Gurashi' },
]

const AvatarFrame = styled(Flex)`
  position: relative;
  width: 50%;
  min-width: 80px;
  max-width: 160px;
  aspect-ratio: 1;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  border: 2px solid ${palette('primary', 0)};
  background-color: ${palette('secondary', 5)};
`

const GenerateButton = styled(Button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: absolute;
  bottom: 0;
  right: 0;
`

const StrikeText = styled.span`
  ${ifProp('striked', css`
    text-decoration: line-through;
    text-decoration-color: ${palette('error', 0)};
    text-decoration-style: solid;
    text-decoration-thickness: 3px;
  `)}
`

const StampText = styled.span`
  position: absolute;
  color: #FF0000;
  bottom: -24%;
  right: 10%;
  font-size: 30px;
  font-weight: bold;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 12px;
  background-color: ${palette('primary', 5)}DD;
  box-shadow: 0 0 4px ${palette('grayscale', 3)}EE;
  animation: stamp 0.6s ease-out forwards;
  animation-delay: 0.3s;
  z-index: 10;
  opacity: 0;

  @keyframes stamp {
    0% {
      opacity: 1;
      transform: scale(2.5) rotate(-12deg);
    }
    30% {
      opacity: 1;
      transform: scale(0.9) rotate(-12deg);
    }
    100% {
      opacity: 1;
      transform: scale(1) rotate(-12deg);
    }
  }
`

const Loader = ({ size, style }) => (
  <Flex
    alignItems="center"
    justifyContent="center"
    style={{
      position: 'absolute',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      inset: 0,
      ...style,
    }}
  >
    <Image src="/logo_simple.png" height={size} className="self_rotate" />
  </Flex>
)

Loader.propTypes = {
  size: PropTypes.string,
  style: PropTypes.object,
}

const AvatarSection = ({ onTopUp, profileFormRef }) => {
  const { auth, setAuth, setAuthUser } = useAuth()
  const [generating, setGenerating] = useState(false)
  const [regenModalOpen, setRegenModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [avatarTooltipOpen, setAvatarTooltipOpen] = useState(false)
  const [selectedAvatarStyle, setSelectedAvatarStyle] = useState('Manga')
  const [discountShown, setDiscountShown] = useState(false)

  const avatarUrl = useMemo(() => {
    return auth.user?.avatar_url || null
  }, [auth.user])

  const emailVerified = useMemo(() => {
    return auth.user?.email_verified || false
  }, [auth.user])

  const noDescription = useMemo(() => {
    return !auth.user?.description || !auth.user?.description.trim()
  }, [auth.user])

  const generateAvatar = useCallback(async () => {
    try {
      setGenerating(true)
      const { data } = await generateMyAvatar({ style: selectedAvatarStyle })
      setAuthUser((oriUser) => ({
        ...oriUser,
        avatar_url: data.image_url,
        user_images: [...(oriUser.user_images || []), data],
      }))
    } catch (err) {
      handleErrorResponse(err, setAuth, '有啲野出錯左！\n你可以改一改個人簡介或者等陣再試>.<')
    } finally {
      setGenerating(false)
    }
  }, [selectedAvatarStyle, setAuth, setAuthUser])

  const onClickEditAvatar = useCallback(() => {
    if (profileFormRef.current?.dirty) {
      return setUnsavedWarningOpen(true)
    }
    setRegenModalOpen(true)
  }, [profileFormRef])

  const onClickConfirmGenerate = useCallback(() => {
    const quotaRemaining = auth.user?.message_remaining
    const isFirstTime = !avatarUrl
    const generationCost = isFirstTime ? 0 : USER_AVATAR_GENERATION_COST
    if (quotaRemaining < generationCost) {
      return onTopUp()
    }
    setRegenModalOpen(false)
    return generateAvatar()
  }, [auth.user, avatarUrl, generateAvatar, onTopUp])

  const onClickDownload = useCallback(async () => {
    try {
      setPreviewLoading(true)
      await downloadFile(avatarUrl, 'crushk-avatar.jpg')
    } catch (err) {
      alert('系統有啲野出錯左！\n請等陣再試>.<')
    } finally {
      setPreviewLoading(false)
    }
  }, [avatarUrl])

  const onClickShare = useCallback(async () => {
    try {
      setPreviewLoading(true)
      await shareFile(avatarUrl, 'crushk-avatar.jpg')
    } catch (err) {
      alert('系統有啲野出錯左！\n請等陣再試>.<')
    } finally {
      setPreviewLoading(false)
    }
  }, [avatarUrl])

  const onCloseAvatarTootip = useCallback(() => {
    setAvatarTooltipOpen(false)
    localStorage.setItem(`${auth.user?.username}_${LOCAL_STORAGE_KEY_AVATAR_GENERATE_TOOLTIP_OPENED}`, true)
    localStorage.setItem(`${auth.user?.username}_${LOCAL_STORAGE_KEY_PROFILE_AVATAR_REMINDER_OPENED}`, true)
  }, [auth.user])

  useEffect(() => {
    if (auth.user && !auth.user?.avatar_url) {
      const opened = localStorage.getItem(`${auth.user?.username}_${LOCAL_STORAGE_KEY_AVATAR_GENERATE_TOOLTIP_OPENED}`)
      if (!opened) {
        setAvatarTooltipOpen(true)
      }
    }
  }, [auth.user])

  useEffect(() => {
    if (regenModalOpen && !avatarUrl) {
      setTimeout(() => setDiscountShown(true), 400)
    }
    setDiscountShown(false)
  }, [avatarUrl, regenModalOpen])

  return (
    <AvatarFrame>
      {avatarUrl ? (
        <Image
          key={avatarUrl}
          src={avatarUrl}
          width="100%"
          height="100%"
          onClick={() => {
            setPreviewLoading(false)
            setPreviewModalOpen(true)
          }}
          style={{ cursor: 'pointer' }}
          circle
        />
      ) : (
        <Text style={{ fontSize: 80 }}>🤖</Text>
      )}
      <GenerateButton
        id="generate-button"
        padding={0}
        disabled={generating}
        onClick={onClickEditAvatar}
      >
        <GrEdit size={18} />
      </GenerateButton>
      {generating && (
        <Loader size="3rem" style={{ borderRadius: '50%' }} />
      )}
      <Tooltip
        place="bottom-start"
        anchorId="generate-button"
        open={avatarTooltipOpen}
      >
        <Text palette="white">
          {'試下搵 Crush仔🤖\n幫你度身訂造一個頭像！'}
        </Text>
        <Flex justifyContent="flex-end" mt={1}>
          <Button
            palette="grayscale"
            padding="0.25rem 0.5rem"
            onClick={onCloseAvatarTootip}
          >
            <Text palette="white" variant="body2">知道</Text>
          </Button>
        </Flex>
      </Tooltip>
      <Modal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        showCloseBtn
      >
        <Image
          key={avatarUrl}
          src={avatarUrl}
          width="100%"
          height="100%"
          borderRadius="0.5rem"
          style={{ maxHeight: '78vh', objectFit: 'contain' }}
        />
        <Flex width={1} mt="0.5rem">
          <Button padding="0.5rem" onClick={onClickDownload}>
            <IoSaveOutline size={28} />
          </Button>
          <Button
            padding="0.5rem"
            onClick={onClickShare}
            style={{ marginLeft: '0.5rem' }}
          >
            <IoShareSocialOutline size={28} />
          </Button>
        </Flex>
        {previewLoading && <Loader size="4rem" />}
      </Modal>
      <Modal
        isOpen={unsavedWarningOpen}
        onClose={() => setUnsavedWarningOpen(false)}
        showCloseBtn
      >
        <Flex
          width={1}
          alignItems="center"
          flexDirection="column"
          maxWidth={240}
        >
          <Text variant="body1" align="center">
            ⚠️ 你有未儲存嘅個人資料改動，請先儲存再生成頭像！
          </Text>
          <Button
            palette="primary"
            onClick={() => setUnsavedWarningOpen(false)}
            style={{ marginTop: '1rem' }}
          >
            返回
          </Button>
        </Flex>
      </Modal>
      <Modal
        isOpen={regenModalOpen}
        onClose={() => setRegenModalOpen(false)}
        style={{ overflow: 'visible' }}
        showCloseBtn
      >
        <Flex
          width={1}
          alignItems="center"
          flexDirection="column"
          minWidth={240}
          maxWidth={360}
          p="1rem"
        >
          {(emailVerified && !noDescription) ? (
            <Text variant="h3" align="center" style={{ position: 'relative' }}>
              Crush仔🤖 會根據你嘅個人資料，為你度身訂造一個頭像，
              <StrikeText striked={discountShown}>
                需要10個用量
              </StrikeText>
              {discountShown && (
                <StampText>首次免費</StampText>
              )}
            </Text>
          ) : (
            <>
              <Text variant="h2" align="center" palette="error">⚠️ 注意 ⚠️</Text>
              <Flex flexDirection="column" alignItems="center" marginTop="1rem">
                <Text variant="h3" palette="primary" align="center">
                  請先{!emailVerified ? '驗證' : '填寫'}
                  <b>{!emailVerified ? ' E-mail👆🏻' : ' 個人簡介👇🏻'}</b>
                </Text>
                <Text variant="h3" palette="primary" align="center">以使用頭像生成功能</Text>
              </Flex>
            </>
          )}
          <Flex mt="2rem" width={1} maxWidth="15rem">
            <Box flex={3}>
              <Select
                value={selectedAvatarStyle}
                onChange={setSelectedAvatarStyle}
                options={avatarStyleOptions}
                disabled={!emailVerified || noDescription}
              />
            </Box>
            <Box flex={2} ml="1rem">
              <Button
                onClick={onClickConfirmGenerate}
                disabled={!emailVerified || noDescription}
                fullWidth
              >
                去馬！
              </Button>
            </Box>
          </Flex>
          <Flex mt="1rem" width={1} maxWidth="7rem">
            <Button
              palette="error"
              variant="outline"
              onClick={() => setRegenModalOpen(false)}
              fullWidth
            >
              返回
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </AvatarFrame>
  )
}

AvatarSection.propTypes = {
  onTopUp: PropTypes.func,
  profileFormRef: PropTypes.shape({
    current: PropTypes.object,
  }),
}

AvatarSection.defaultProps = {
  onTopUp: () => {},
  profileFormRef: null,
}

export default AvatarSection
